import React, { Suspense, lazy, useEffect } from 'react';
import {
  Routes,
  Route,
  BrowserRouter as Router,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import { Box, Spinner } from '@chakra-ui/react';

import Home from './screens/Home';
import NotFound from './screens/NotFound';
import ProtectedAdminRoute from './components/admin/ProtectedAdminRoute';
import AdminLayout from './admin/components/layout/AdminLayout';
import { PRODUCT_CATEGORIES } from './config/navigation';

const Services = lazy(() => import('./screens/Services'));
const Products = lazy(() => import('./screens/Products'));
const Projects = lazy(() => import('./screens/Projects'));
const SiesaApp = lazy(() => import('./screens/SiesaApp'));
const About = lazy(() => import('./screens/About'));
const Contact = lazy(() => import('./screens/ContactUs'));
const Legal = lazy(() => import('./screens/Legal'));
const Login = lazy(() => import('./components/Login'));

const AdmIndex = lazy(() => import('./screens/admin/Index'));
const AdmProducts = lazy(() => import('./screens/admin/Products'));
const AdmProjects = lazy(() => import('./screens/admin/Projects'));
const AdmUsers = lazy(() => import('./screens/admin/Users'));

function Loading() {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minH="60vh">
      <Spinner size="xl" color="accent.default" thickness="3px" />
    </Box>
  );
}

function RedirectProductCategory() {
  const { categorySlug } = useParams();
  const category = PRODUCT_CATEGORIES.find(
    item => item.slug === categorySlug || item.legacySlug === categorySlug
  );
  return (
    <Navigate
      to={`/products/${category ? category.slug : categorySlug}`}
      replace
    />
  );
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      window.requestAnimationFrame(() => {
        document.getElementById(hash.slice(1))?.scrollIntoView();
      });
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, hash]);

  return null;
}

export default function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services" element={<Services />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:categorySlug" element={<Products />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/siesa-qr-app" element={<SiesaApp />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/privacy-policy"
            element={<Legal document="privacidad" />}
          />
          <Route path="/terms" element={<Legal document="terminos" />} />

          {/* Compatibilidad con las URLs anteriores en español. */}
          <Route
            path="/servicios"
            element={<Navigate to="/services" replace />}
          />
          <Route
            path="/productos"
            element={<Navigate to="/products" replace />}
          />
          <Route
            path="/productos/:categorySlug"
            element={<RedirectProductCategory />}
          />
          <Route
            path="/proyectos"
            element={<Navigate to="/projects" replace />}
          />
          <Route path="/nosotros" element={<Navigate to="/about" replace />} />
          <Route
            path="/contacto"
            element={<Navigate to="/contact" replace />}
          />
          <Route
            path="/contact-us"
            element={<Navigate to="/contact" replace />}
          />
          <Route
            path="/politica-de-privacidad"
            element={<Navigate to="/privacy-policy" replace />}
          />
          <Route
            path="/terminos-y-condiciones"
            element={<Navigate to="/terms" replace />}
          />

          <Route path="/admin/login" element={<Login />} />
          <Route
            path="/admin"
            element={(
              <ProtectedAdminRoute>
                <AdminLayout />
              </ProtectedAdminRoute>
            )}
          >
            <Route path="index" element={<AdmIndex />} />
            <Route path="products" element={<AdmProducts />} />
            <Route path="projects" element={<AdmProjects />} />
            <Route path="users" element={<AdmUsers />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
